import "swiper/css";
import "swiper/css/navigation";

import cn from "classnames";
import { useState, useMemo } from "react";
import { useRef, ElementRef } from "react";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import { Navigation, Mousewheel, Keyboard } from "swiper/modules";

import { Preload } from "@/src/component/partial/Preload";
import { QUERY_PARAMS, KEYS } from "@/src/const";
import DynamicPageLogic from "@/src/logic/DynamicPageLogic";
import TrackingLogic from "@/src/logic/TrackingLogic";
import { FrontendTrackingService } from "@/src/service";
import { StrapiCollectionType, BrokerProfileType, StrapiComponentType, StrapiSingleType } from "@/src/type";
import { NextButtonPosition, NextIconColor } from "@/src/type/strapi/collection";
import { StringHelper, Log } from "@/src/util";

import DynamicPageAlternate from "./DynamicPageAlternate";
import DynamicePageEffects from "./DynamicPageEffects";
import DynamicPageSection from "./DynamicPageSection";

import styles from "./DynamicPage.module.scss";

interface DynamicPageProps {
  page: StrapiCollectionType.DynamicPage;
  searchParams?: Record<string, string | string[] | undefined>;
  profile?: BrokerProfileType.BrokerProfile | null;
  refreshId: string;
  mobileHint?: boolean;
  deviceWidthHint?: number;
  appleOrMobileHint?: boolean;
  userAgent?: string;
  domain?: string;
  headers?: any;
  defaultNextButtonText?: string;
  defaultNextIconColor: NextIconColor;
  defaultNextButtonPosition?: NextButtonPosition;
  defaultActiveId?: number;
  webConfig?: StrapiSingleType.WebConfig;
  destinations?: StrapiCollectionType.Destination[];
}

const defaultDynamicPageSettings: StrapiComponentType.Object.SwiperVertical = {
  keyboard: true,
  keyboardOnlyInViewport: true,
  keyboardPageUpDown: true,
  mousewheel: true,
  mousewheelReleaseOnEdges: false,
  mousewheelForceToAxis: true,
  mousewheelSensitivity: 0.75,
  mousewheelThresholdDelta: 10,
  speed: 400,
};

function DynamicPage({
  page,
  searchParams,
  profile,
  refreshId,
  mobileHint,
  deviceWidthHint,
  appleOrMobileHint,
  userAgent,
  domain,
  headers,
  defaultNextButtonText,
  defaultNextIconColor,
  defaultNextButtonPosition,
  defaultActiveId,
  webConfig,
  destinations,
}: DynamicPageProps) {
  // variables
  const snapToGrid = page.layout === "main";
  const active = defaultActiveId ?? parseInt(StringHelper.checkIsStringOrIsNull(searchParams?.[QUERY_PARAMS.ACTIVE], "0"), 10);
  const initialSlide = typeof active === "number" && active > 0 ? active : undefined;
  const hasProfileOrPromo = profile?.firstName || page?.promo_profile_widget?.data?.attributes?.title;
  const profileDrawerActive: number | undefined = hasProfileOrPromo ? 86 : undefined;
  const next = "dynamic-page-next";

  // state & hooks
  const [swiper, setSwiper] = useState<any>(null);
  const quality = webConfig?.defaultImageQuality ?? 100;
  const dynamicPageSettings = webConfig?.dynamicPageEffects ?? defaultDynamicPageSettings;
  const disableCssMode = (webConfig?.enableSwiperEffectsNonWindows && appleOrMobileHint) || !appleOrMobileHint;
  const cssMode = disableCssMode ? (dynamicPageSettings?.cssMode ?? false) : true;

  // refs
  const effectsRef = useRef<ElementRef<typeof DynamicePageEffects>>(null);

  /**
   * swiper slides are used for both swiper layout (snapToGrid) or non-swiper layouts
   */
  const slides = useMemo(() => {
    Log.logToConsoleDebug(
      "view/DynamicPage.tsx",
      "Dynamic Page Loaded / Slides Changed",
      [page, { defaultActiveId, initialSlide, searchParams, snapToGrid, deviceWidthHint, mobileHint, quality, userAgent, domain, headers }],
      Log.LOG_COLORS.STATE_CHANGE.BG_COLOR,
      Log.LOG_COLORS.STATE_CHANGE.COLOR
    );

    const filtered = page.dynamic_page_sections.data.filter((opt) =>
      DynamicPageLogic.checkIfSectionShouldTurnOff(searchParams, opt.attributes.hideOnSearchParams)
    );

    return filtered.map((data, i) => (
      <SwiperSlide
        key={`${data.attributes.sectionId}_${data.id}`}
        className={cn(styles.card, page.layout !== "main" ? styles.alternate : undefined)}
        id={`SwiperSlide_${i}_${data.id}`}
      >
        <DynamicPageSection
          key={`${data.attributes.sectionId}_${data.id}`}
          section={data.attributes}
          snapToGrid={snapToGrid}
          slug={page.slug}
          index={i}
          initialSlide={initialSlide}
          bottomDrawerHeight={profileDrawerActive}
          searchParams={searchParams}
          contactUsLink={page.contactUsLink}
          contactUsButtonText={page.contactUsButtonText}
          mobileHint={mobileHint}
          deviceWidthHint={deviceWidthHint}
          quality={quality}
          extraSectionsToEagerLoad={page.extraSectionsToEagerLoad}
          userAgent={userAgent}
          layout={page.layout}
          destinations={destinations}
          calendarConfig={page.calendar_config}
          sectionTypePath={TrackingLogic.constructToSectionTypePath({
            pageId: page.id,
            sectionId: data.id,
            sectionType: data.attributes?.type?.[0]?.__component ?? "",
            sectionTypeId: data.attributes?.type?.[0]?.id ?? "",
          })}
        />
      </SwiperSlide>
    ));
  }, [
    page,
    snapToGrid,
    initialSlide,
    profileDrawerActive,
    searchParams,
    mobileHint,
    deviceWidthHint,
    quality,
    userAgent,
    domain,
    headers,
    defaultActiveId,
    destinations,
  ]);

  if (!Array.isArray(page.dynamic_page_sections?.data) || (page.dynamic_page_sections?.data ?? []).length === 0) {
    return <></>;
  }

  if (!snapToGrid) {
    return (
      <>
        <DynamicPageAlternate
          page={page}
          refreshId={refreshId}
        />
        <>{slides}</>
        <div id={KEYS.ELEMENT_IDS.FOOTER_PADDING} />
      </>
    );
  }

  return (
    <>
      <Preload images={["/image/icon_arrow_down_thick_dark.svg", "/image/icon_arrow_down_thick.svg"]} />
      <div
        id="eventCaptureWall"
        suppressHydrationWarning
      />
      <Swiper
        id={KEYS.DYNAMIC_PAGE_SWIPER}
        userAgent={userAgent}
        key={refreshId}
        className={cn(styles.swiper)}
        modules={[Navigation, Mousewheel, Keyboard]}
        direction="vertical"
        slidesPerView={1}
        spaceBetween={0}
        speed={dynamicPageSettings?.speed ?? 500}
        initialSlide={initialSlide}
        edgeSwipeDetection={dynamicPageSettings?.edgeSwipeDetection ?? false}
        longSwipes={dynamicPageSettings?.longSwipes ?? false}
        longSwipesRatio={dynamicPageSettings?.longSwipesRatio ?? 0.5}
        longSwipesMs={dynamicPageSettings?.longSwipesMs ?? 300}
        shortSwipes={dynamicPageSettings?.shortSwipes ?? true}
        threshold={dynamicPageSettings?.touchThreshold ?? 5}
        touchAngle={dynamicPageSettings?.touchAngle ?? 45}
        touchRatio={dynamicPageSettings?.touchRatio ?? 1}
        touchMoveStopPropagation={dynamicPageSettings?.touchMoveStopPropagation ?? false}
        touchReleaseOnEdges={dynamicPageSettings?.touchReleaseOnEdges}
        followFinger={dynamicPageSettings?.followFinger ?? false}
        cssMode={cssMode}
        touchStartPreventDefault={false}
        simulateTouch={true}
        allowTouchMove={true}
        passiveListeners={true}
        preventClicks={false}
        preventClicksPropagation={false}
        focusableElements="a, button, input, textarea, select, radio, checkbox"
        noSwiping={true}
        noSwipingClass="swiper-no-swiping"
        keyboard={
          dynamicPageSettings?.keyboard
            ? {
                enabled: dynamicPageSettings?.keyboard,
                onlyInViewport: dynamicPageSettings?.keyboardOnlyInViewport,
                pageUpDown: dynamicPageSettings?.keyboardPageUpDown,
              }
            : false
        }
        mousewheel={
          dynamicPageSettings?.mousewheel
            ? {
                enabled: dynamicPageSettings?.mousewheel,
                releaseOnEdges: dynamicPageSettings?.mousewheelReleaseOnEdges,
                sensitivity: dynamicPageSettings?.mousewheelSensitivity,
                forceToAxis: dynamicPageSettings?.mousewheelForceToAxis,
                thresholdDelta: dynamicPageSettings?.mousewheelThresholdDelta,
                thresholdTime: dynamicPageSettings?.mousewheelThresholdTime,
              }
            : false
        }
        onSwiper={setSwiper}
        onSlideChange={(swiper: SwiperClass) => {
          window.dispatchEvent(new MessageEvent("swiperSlideChange", { data: { activeIndex: swiper.activeIndex } }));
          setTimeout(() => {
            window.dispatchEvent(new MessageEvent("swiperSlideChange", { data: { activeIndex: swiper.activeIndex } }));
          }, 300);
          effectsRef?.current?.checkSection(swiper?.activeIndex);
          if (typeof swiper?.destroy !== "boolean") {
            FrontendTrackingService.postTrackingLog({
              action: "verticalCardChanged",
              actionId: `DynamicPage:${page.id}`,
              actionIdType: "componentName:componentId",
              actionMessage: `Dynamic Page changes with ${page.name} and index slide ${(swiper?.activeIndex ?? 0) + 1}`,
              triggeredFrom: "DynamicPage.tsx(onSlideChange)",
            });
          }
        }}
        navigation={{
          nextEl: `#${next}`,
        }}
      >
        {slides}
      </Swiper>
      <DynamicePageEffects
        ref={effectsRef}
        page={page}
        searchParams={searchParams}
        mobileHint={mobileHint}
        deviceWidthHint={deviceWidthHint}
        appleOrMobileHint={appleOrMobileHint}
        active={active}
        initialSlide={initialSlide}
        next={next}
        profileDrawerActive={profileDrawerActive}
        swiper={swiper}
        profile={profile}
        defaultNextButtonText={defaultNextButtonText}
        defaultNextIconColor={defaultNextIconColor}
        defaultNextButtonPosition={defaultNextButtonPosition}
      />
    </>
  );
}

export default DynamicPage;
